import type { ViewSize } from '@mimir/client-common/util/premiere';
import {
  getDialogChoices, getViewSize,
  setViewSize, getLastSelectedFormIds,
  setLastSelectedFormId,
  isValidItemTypeForLastSelectedFormIds,
  type LastSelectedFormIds,
} from '../util/localStorage';
import type { Bookmark, BookmarkViewModel, EditBookmarkParams, UserSettings, Theme, Language, SubtitleDefaultOptions } from '@mimir/common/dto/userSettingsModels';
import type { ParsedFilter } from '@mimir/common/filters/filter-util';
import type { DialogChoices } from '../model/dialogChoices';
import { generate as shortid } from 'shortid';
import { saveUserSettings as apiSaveUserSettings } from '@mimir/web/util/api';
import { logErrorAndShowToast } from '../util/uiError';
import type { Filter} from '@mimir/common/filters/Filters';
import { getParsedFilters } from '@mimir/common/filters/Filters';
import { setDialogChoices as saveDialogChoicesInLocalStorage } from '@mimir/web/util/localStorage';
import { vueSet, reactive } from '@mimir/web/compat/vueCompat';
import type { SearchItemType } from '@mimir/common/dto/itemTypes';
import type { BookmarkFolder } from '@mimir/common/dto/bookmarkDto';

interface UserSettingsState {
  viewSize: ViewSize;
  bookmarkModels: BookmarkViewModel[],
  bookmarksFolders: BookmarkFolder[];
  lastSeenChangelogId: string | null,
  workFolderId: string | null,
  // TODO(dorjoy): Remove when removing 'showSetFiltersAsDefault' feature flag
  defaultFilters: ParsedFilter[],
  dismissedNotifications: Record<string, boolean>,
  showRenameSubclipDialog: boolean,
  showSequenceParts: boolean,
  dialogChoices: DialogChoices,
  language: Language,
  theme: Theme,
  lastSelectedFormIds: LastSelectedFormIds,
  subtitlePreference: SubtitleDefaultOptions,
  showPopups: boolean,
  hasBootstrapped: boolean,
  pinnedItems: string[],
}

export const userSettings: UserSettingsState = reactive({
  viewSize: getViewSize(),
  bookmarkModels: [],
  bookmarksFolders: [],
  workFolderId: null,
  // TODO(dorjoy): Remove when removing 'showSetFiltersAsDefault' feature flag
  defaultFilters: [],
  dismissedNotifications: {},
  markedAsReadUserNotifications: {},
  lastSeenChangelogId: null,
  showRenameSubclipDialog: false,
  showSequenceParts: true,
  dialogChoices: getDialogChoices(),
  language: 'en',
  theme: 'dark',
  lastSelectedFormIds: getLastSelectedFormIds(),
  subtitlePreference: 'Off',
  showPopups: true,
  /**
   * Defines if the user is loading the app for the first time.
   */
  hasBootstrapped: false,
  pinnedItems: [],
});

export function setHasBootstrappedUserSettings(val: boolean) {
  userSettings.hasBootstrapped = val;
}

export function saveBookmarkFolders(folders: BookmarkFolder[]) {
  userSettings.bookmarksFolders = folders;
}

export function saveUserSettingsViewSize(size: ViewSize) {
  userSettings.viewSize = size;
  setViewSize(size);
}

function setLastSeenChangelogId(val: string | null) {
  userSettings.lastSeenChangelogId = val;
}

function addBookmark(bookmark: BookmarkViewModel) {
  userSettings.bookmarkModels.push(bookmark);
}

function addBookmarkFolder(folder: BookmarkFolder) {
  userSettings.bookmarksFolders.push(folder);
}

function deleteBookmarkFolder(folderId: string) {
  const index = userSettings.bookmarksFolders.findIndex(f => f.id === folderId);
  if (index >= 0) {
    const updates = deleteNestedFolders(userSettings.bookmarksFolders, folderId);
    userSettings.bookmarksFolders = updates.remainingFolders;
    for (const bookmarkId of updates.deletedFolderIds) {
      const bmIndex = userSettings.bookmarkModels.findIndex(bm => bm.parentFolder === bookmarkId);
      if (bmIndex >= 0) {
        userSettings.bookmarkModels.splice(bmIndex, 1);
      }
    }
  }
}

function deleteNestedFolders(folders: BookmarkFolder[], folderIdToDelete: string): { remainingFolders: BookmarkFolder[], deletedFolderIds: string[] } {
  const deletedFolderIds: string[] = [];
  const queue = [folderIdToDelete];

  while (queue.length > 0) {
    const currentFolderId = queue.shift();
    if (!currentFolderId) {
      continue;
    }
    deletedFolderIds.push(currentFolderId);

    for (let i = 0; i < folders.length; i++) {
      if (folders[i].parentFolder === currentFolderId) {
        queue.push(folders[i].id);
      }
    }
  }

  const remainingFolders = folders.filter(folder => !deletedFolderIds.includes(folder.id));
  return  { remainingFolders: remainingFolders, deletedFolderIds: deletedFolderIds };
}

function replaceBookmarkFolder(folder: BookmarkFolder) {
  const index = userSettings.bookmarksFolders.findIndex(f => f.id === folder.id);
  if (index >= 0) {
    userSettings.bookmarksFolders.splice(index, 1, folder);
  }
}

function editBookmark(params: EditBookmarkParams) {
  const bookmark = userSettings.bookmarkModels.find(bm => bm.id === params.id);
  if (bookmark) {
    bookmark.icon = params.icon;
    bookmark.title = params.title;
    bookmark.path = params.path;
    bookmark.folderTreePath = params.folderTreePath;
    bookmark.parentFolder = params.parentFolder;
    bookmark.sortKey = params.sortKey;
  }
}

export function setWorkFolderId(folderId: string | null) {
  userSettings.workFolderId = folderId;
}


export function saveWorkFolderId(folderId: string | null) {
  setWorkFolderId(folderId);
  void saveUserSettings();
}

export function setShowRenameSubclipDialog(val: boolean) {
  userSettings.showRenameSubclipDialog = val;
}

function setDefaultFilters(filters: ParsedFilter[]) {
  userSettings.defaultFilters = filters;
}

export function dismissNotification(key: string) {
  vueSet(userSettings.dismissedNotifications, key, true);
}

function showSequenceParts(show: boolean) {
  userSettings.showSequenceParts = show;
}

function setLanguage(language: Language) {
  userSettings.language = language;
}

function setTheme(theme: Theme) {
  userSettings.theme = theme;
}

function setPopups(show: boolean) {
  userSettings.showPopups = show;
}

export function saveLastSelectedFormId(id : string, type: SearchItemType) {
  if (isValidItemTypeForLastSelectedFormIds(type)) {
    userSettings.lastSelectedFormIds[type] = id;
    setLastSelectedFormId(id, type);
  }
}

/**
 * For item creation dialogs to remember the last selected form.
 * @param type The search item type
 * @returns The last selected form id or null if none is set
 */
export function getLastSelectedFormId(type: SearchItemType) {
  if (!isValidItemTypeForLastSelectedFormIds(type)) {
    return null;
  }
  return userSettings.lastSelectedFormIds[type];
}

function setSubtitle(subtitleOption: SubtitleDefaultOptions) {
  userSettings.subtitlePreference = subtitleOption;
}

function setPinnedItems(items: string[]) {
  userSettings.pinnedItems = items;
}

export function setUserSettings(settings: UserSettings) {
  const newBookmarks: BookmarkViewModel[] = settings.bookmarks.map(b => {
    return {
      ...b,
      id: b.id || shortid(),
      type: 'user',
    };
  });
  userSettings.bookmarkModels.splice(0, userSettings.bookmarkModels.length, ...newBookmarks);
  userSettings.bookmarksFolders = settings.bookmarksFolders || [];
  setWorkFolderId(settings.workFolderId ?? null);
  setDefaultFilters(settings.defaultFilters ?? []);
  userSettings.dismissedNotifications = settings.dismissedNotifications ?? {};
  setLastSeenChangelogId(settings.lastSeenChangelogId ?? null);
  setShowRenameSubclipDialog(settings.showRenameSubclipDialog ?? false);
  showSequenceParts(settings.showSequenceParts ?? true);
  setLanguage(settings.language ?? 'en');
  setTheme(settings.theme ?? 'os');
  setSubtitle(settings.subtitlePreference ?? 'Off');
  setPopups(settings.showPopups ?? true);
  setPinnedItems(settings.pinnedItems ?? []);
}

/**
  * Saves the user settings to the backend.
 * @returns True if the user settings were saved successfully, false otherwise.
 */
export async function saveUserSettings(): Promise<boolean> {
  try {
    const workFolderId = userSettings.workFolderId !== null ? userSettings.workFolderId : undefined;
    const bookmarks: Bookmark[] = userSettings.bookmarkModels.map(model => {
      return {
        id: model.id,
        title: model.title,
        icon: model.icon,
        path: model.path,
        isFilterBookmark: model.isFilterBookmark,
        folderTreePath: model.folderTreePath,
        parentFolder: model.parentFolder,
        sortKey: model.sortKey,
      };
    });
    await apiSaveUserSettings({
      bookmarks,
      bookmarksFolders: userSettings.bookmarksFolders || undefined,
      workFolderId,
      defaultFilters: userSettings.defaultFilters,
      dismissedNotifications: userSettings.dismissedNotifications,
      lastSeenChangelogId: userSettings.lastSeenChangelogId ?? undefined,
      showRenameSubclipDialog: userSettings.showRenameSubclipDialog ?? false,
      showSequenceParts: userSettings.showSequenceParts,
      language: userSettings.language,
      theme: userSettings.theme,
      subtitlePreference: userSettings.subtitlePreference,
      showPopups: userSettings.showPopups,
      pinnedItems: userSettings.pinnedItems,
    });
    return true;
  } catch (err) {
    logErrorAndShowToast('Failed to save user settings, please try again later.', err, 'ex in saveUserSettings');
    return false;
  }
}

export function dismissAndSaveNotification(key: string) {
  dismissNotification(key);
  void saveUserSettings();
}

export function seenLastChangelogId(latestLogId: string) {
  setLastSeenChangelogId(latestLogId);
  void saveUserSettings();
}

export async function addAndSaveBookmark(bookmark: BookmarkViewModel) {
  addBookmark(bookmark);
  await saveUserSettings();
}

export async function addAndSaveBookmarkFolder(folder: BookmarkFolder) {
  addBookmarkFolder(folder);
  await saveUserSettings();
}

export async function replaceAndSaveBookmarkFolder(folder: BookmarkFolder) {
  replaceBookmarkFolder(folder);
  await saveUserSettings();
}

export async function deleteAndSaveBookmarkFolder(folderId: string) {
  deleteBookmarkFolder(folderId);
  await saveUserSettings();
}

export async function editAndSaveBookmark(params: EditBookmarkParams) {
  editBookmark(params);
  await saveUserSettings();
}

export async function saveDefaultFilters(filters: Filter<any>[]) {
  setDefaultFilters(getParsedFilters(filters));
  await saveUserSettings();
}

export function saveShowSequenceParts(show: boolean) {
  showSequenceParts(show);
  void saveUserSettings();
}

export function saveDialogChoices(choices: DialogChoices) {
  userSettings.dialogChoices = choices;
  saveDialogChoicesInLocalStorage(choices);
}

function deleteBookmark(bookmark: BookmarkViewModel) {
  const index = userSettings.bookmarkModels.findIndex(b => b.id === bookmark.id);
  if (index >= 0) {
    userSettings.bookmarkModels.splice(index, 1);
  }
}

export async function deleteAndSaveBookmark(bookmark: BookmarkViewModel) {
  deleteBookmark(bookmark);
  await saveUserSettings();
}

export async function saveLanguage(language: Language) {
  setLanguage(language);
  await saveUserSettings();
}

export async function saveTheme(theme: Theme) {
  setTheme(theme);
  await saveUserSettings();
}

export async function saveSubtitle(subtitleOption: SubtitleDefaultOptions) {
  setSubtitle(subtitleOption);
}

export async function savePopups(show: boolean) {
  setPopups(show);
  await saveUserSettings();
}

export async function pinItems(itemIds: string[]) {
  const prevPinned = [...userSettings.pinnedItems];
  const newItems = itemIds.filter(id => !userSettings.pinnedItems.includes(id));
  if (newItems.length > 0) {
    userSettings.pinnedItems.push(...newItems);
    const success = await saveUserSettings();
    if (!success) {
      userSettings.pinnedItems = prevPinned;
    }
    return success;
  }
}

export async function unpinItems(itemIds: string[]) {
  const prevPinned = [...userSettings.pinnedItems];
  const originalLength = userSettings.pinnedItems.length;
  userSettings.pinnedItems = userSettings.pinnedItems.filter(id => !itemIds.includes(id));
  if (userSettings.pinnedItems.length !== originalLength) {
    const success = await saveUserSettings();
    if (!success) {
      userSettings.pinnedItems = prevPinned;
    }
    return success;
  }
}

export function isPinned(itemId: string): boolean {
  return userSettings.pinnedItems.includes(itemId);
}
